import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";

export default function MenuPage() {
  const { code } = useParams();
  const [menu, setMenu] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    async function load() {
      const { data: qr } = await supabase.from("qr_codes")
        .select("menu_id").eq("code", code).single();
      if (!qr?.menu_id) { setLoading(false); return; }

      const { data: menuData } = await supabase.from("menus")
        .select("*").eq("id", qr.menu_id).single();
      const { data: cats } = await supabase.from("menu_categories")
        .select("*, menu_items(*)")
        .eq("menu_id", qr.menu_id)
        .order("sort_order");

      setMenu(menuData);
      setCategories(cats || []);
      setActiveCategory(cats?.[0]?.id || null);
      setLoading(false);
    }
    load();
  }, [code]);

  if (loading) return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!menu) return (
    <div className="min-h-screen bg-[#020617] flex items-center justify-center">
      <p className="text-white text-sm">Menu not found.</p>
    </div>
  );

  const allItems = categories.flatMap(c => (c.menu_items || []).map(i => ({ ...i, categoryName: c.name })));
  const filteredItems = search
    ? allItems.filter(i => i.name.toLowerCase().includes(search.toLowerCase()) || (i.description || "").toLowerCase().includes(search.toLowerCase()))
    : (activeCategory ? (categories.find(c => c.id === activeCategory)?.menu_items || []) : allItems);

  return (
    <div className="min-h-screen bg-[#020617] text-white">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#020617]/95 backdrop-blur-xl border-b border-white/10 px-4 py-4">
        <h1 className="text-xl font-black uppercase italic tracking-tighter mb-3">{menu.name}</h1>
        <input
          type="text"
          placeholder="Search menu..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-white text-sm outline-none focus:border-blue-500 mb-3"
        />
        {!search && (
          <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {categories.map(cat => (
              <button key={cat.id} onClick={() => setActiveCategory(cat.id)}
                className={`shrink-0 px-4 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${activeCategory === cat.id ? "bg-blue-600 text-white" : "bg-white/5 text-gray-500"}`}>
                {cat.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Items */}
      <div className="px-4 py-4 space-y-3 max-w-lg mx-auto">
        {filteredItems.length === 0 ? (
          <p className="text-gray-500 text-center py-10">No items found.</p>
        ) : filteredItems.map(item => (
          <div key={item.id} className={`bg-white/5 border border-white/10 rounded-2xl overflow-hidden ${!item.is_available ? "opacity-40" : ""}`}>
  {item.image_url && (
    <img src={item.image_url} alt={item.name}
      className="w-full h-40 object-cover"
      onError={e => { e.target.style.display = 'none'; }}
    />
  )}
  <div className="p-4 flex items-start gap-3">
    <span className="text-lg mt-0.5">{item.is_veg ? "🟢" : "🔴"}</span>
    <div className="flex-1 min-w-0">
      <p className="text-white font-black text-sm">{item.name}</p>
      {item.description && <p className="text-gray-400 text-[11px] mt-0.5">{item.description}</p>}
      {!item.is_available && <p className="text-red-400 text-[9px] font-black uppercase mt-1">Currently Unavailable</p>}
    </div>
    <p className="text-white font-black shrink-0">{menu.currency}{item.price}</p>
  </div>
</div>
        ))}
      </div>

      <div className="text-center py-8">
        <p className="text-gray-700 text-[9px] uppercase tracking-widest">Powered by Digitoly QR</p>
      </div>
    </div>
  );
}